import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useVenueSearch, type VenueSearchResult } from '@/features/venues/useVenueSearch';
import { Icon } from '@/components/ui/icon';
import { BadgeCheckIcon, Building2Icon, MapPinIcon, PlusIcon, SearchIcon } from 'lucide-react-native';
import * as React from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';

export type VenueResult = VenueSearchResult;

interface Props {
  value: VenueResult | null;
  onChange: (venue: VenueResult) => void;
  onVenueNotListed?: (typedQuery: string) => void;
  placeholder?: string;
}

export function VenuePicker({ value, onChange, onVenueNotListed, placeholder = 'Search venues...' }: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const venueSearch = useVenueSearch(query, open);

  const displayName = value?.name?.trim() || '';
  const displayAddress = value?.address?.trim() || '';
  const results = venueSearch.data?.venues ?? [];
  const queryText = query.trim();
  const showManualFallback = Boolean(onVenueNotListed) && queryText.length >= 2;

  function handleOpen() {
    setQuery(value?.name ?? '');
    setOpen(true);
  }

  function handleSelect(venue: VenueResult) {
    onChange(venue);
    setOpen(false);
  }

  function handleNotListed() {
    onVenueNotListed?.(queryText);
    setOpen(false);
  }

  function handleClear() {
    onChange({ name: '' });
  }

  return (
    <>
      <Pressable
        onPress={handleOpen}
        className="border-input bg-background min-h-12 rounded-xl border p-3">
        <View className="flex-row items-center gap-3">
          <View className="bg-muted size-9 items-center justify-center rounded-full">
            <Icon as={Building2Icon} size={16} color="#EF4E2E" />
          </View>
          <View className="flex-1">
            <Text className={displayName ? 'text-foreground' : 'text-muted-foreground'} numberOfLines={1}>
              {displayName || placeholder}
            </Text>
            {displayAddress ? (
              <Text variant="muted" className="mt-1" numberOfLines={1}>
                {displayAddress}
              </Text>
            ) : (
              <Text variant="muted" className="mt-1" numberOfLines={1}>
                Search the venue database or enter it manually
              </Text>
            )}
          </View>
          {displayName ? (
            <Pressable onPress={handleClear} hitSlop={8} className="rounded-full px-2 py-1">
              <Text variant="small" className="text-muted-foreground">
                Clear
              </Text>
            </Pressable>
          ) : null}
        </View>
      </Pressable>

      <Modal visible={open} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setOpen(false)}>
        <View className="bg-background flex-1 px-4 pb-6 pt-4">
          <View className="mb-4 flex-row items-center justify-between gap-3">
            <View>
              <Text variant="large" className="text-foreground">
                Choose a venue
              </Text>
              <Text variant="muted" className="mt-1">
                Pick a venue if you find it. If not, you can enter it manually.
              </Text>
            </View>
            <Button variant="ghost" size="sm" onPress={() => setOpen(false)}>
              <Text>Close</Text>
            </Button>
          </View>

          <View className="border-input bg-input/30 mb-4 flex-row items-center gap-2 rounded-xl border px-3">
            <Icon as={SearchIcon} size={16} className="text-muted-foreground" />
            <Input
              value={query}
              onChangeText={setQuery}
              placeholder="Search venues…"
              autoFocus
              className="border-0 bg-transparent px-0 shadow-none"
            />
          </View>

          <ScrollView keyboardShouldPersistTaps="handled" className="flex-1">
            {venueSearch.isFetching ? (
              <View className="border-border bg-card mb-3 rounded-2xl border p-4">
                <Text variant="muted">Searching venues…</Text>
              </View>
            ) : null}

            {venueSearch.isError ? (
              <View className="border-destructive/30 bg-destructive/10 mb-3 rounded-2xl border p-4">
                <Text className="text-foreground">Venue search is unavailable</Text>
                <Text variant="muted" className="mt-1">
                  {getAPIErrorRecoveryCopy(
                    venueSearch.error,
                    'venue_picker',
                    'Enter the venue manually and add the street address next.'
                  )}
                </Text>
              </View>
            ) : null}

            {!venueSearch.isFetching && !venueSearch.isError && queryText.length >= 2 && results.length === 0 ? (
              <View className="border-border bg-card mb-3 rounded-2xl border p-4">
                <Text className="text-foreground">No venues found</Text>
                <Text variant="muted" className="mt-1">
                  Try a different search or add this venue manually below.
                </Text>
              </View>
            ) : null}

            {results.map((venue) => (
              <Pressable
                key={venue.id ?? `${venue.name}-${venue.address ?? ''}`}
                onPress={() => handleSelect(venue)}
                className="border-border bg-card mb-3 rounded-2xl border p-4 active:opacity-90">
                <View className="flex-row items-start gap-3">
                  <View className="bg-muted mt-0.5 size-9 items-center justify-center rounded-full">
                    <Icon as={Building2Icon} size={16} color="#EF4E2E" />
                  </View>
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2">
                      <Text className="text-foreground flex-1 font-semibold">{venue.name}</Text>
                      {venue.is_verified ? (
                        <View className="bg-primary/10 flex-row items-center gap-1 rounded-full px-2 py-1">
                          <Icon as={BadgeCheckIcon} size={12} className="text-primary" />
                          <Text variant="small" className="text-foreground">
                            Verified
                          </Text>
                        </View>
                      ) : null}
                    </View>
                    {venue.address ? (
                      <View className="mt-2 flex-row items-start gap-2">
                        <Icon as={MapPinIcon} size={14} className="mt-0.5 text-muted-foreground" />
                        <Text variant="muted" className="flex-1">
                          {venue.address}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              </Pressable>
            ))}

            {showManualFallback ? (
              <Pressable
                onPress={handleNotListed}
                className="border-border bg-card mb-3 rounded-2xl border p-4 active:opacity-90">
                <View className="flex-row items-start gap-3">
                  <View className="bg-primary/10 mt-0.5 size-9 items-center justify-center rounded-full">
                    <Icon as={PlusIcon} size={16} className="text-primary" />
                  </View>
                  <View className="flex-1">
                    <Text className="text-foreground font-semibold">
                      Add {queryText} manually
                    </Text>
                    <Text variant="muted" className="mt-1">
                      Create the venue for this event and enter the street address next.
                    </Text>
                  </View>
                </View>
              </Pressable>
            ) : null}
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}
